import React, { useRef, useState, useEffect, useCallback } from 'react'
import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Con lắc đơn (Lớp 11 — Dao động): KÉO vật tới góc θ₀ rồi THẢ → biên độ góc = θ₀.
// Cấp vận tốc đầu v₀ → biên độ lớn hơn. Tăng hệ số cản → dao động TẮT DẦN.
// Phương trình: α = −(g/l)·sinθ − c·ω ; chu kì nhỏ T=2π√(l/g) (KHÔNG phụ thuộc khối lượng).
const G = 9.8
const W = 660, H = 360
const PVX = 175, PVY = 36        // điểm treo
const WIN = 8                    // cửa sổ thời gian đồ thị (s)
const THMAX = 1.35               // giới hạn kéo (~77°)

const SimplePendulumSimulation: React.FC<Props> = ({
  title = 'Con lắc đơn — kéo & thả (tắt dần)',
  description = 'Kéo vật tới góc θ₀ rồi thả: biên độ góc bằng θ₀. Cấp vận tốc đầu v₀ → biên độ lớn hơn. Tăng hệ số cản → dao động tắt dần. Chu kì T=2π√(l/g) không phụ thuộc khối lượng.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [l, setL] = useState(1.2)    // m
  const [c, setC] = useState(0)      // hệ số cản
  const [v0, setV0] = useState(0)    // m/s (tiếp tuyến)
  const [showForce, setShowForce] = useState(true)  // hiện lực kéo về (thành phần tiếp tuyến của trọng lực)
  const [playing, setPlaying] = useState(false)

  const thRef = useRef(0.5)          // li độ góc hiện tại (rad)
  const omRef = useRef(0)            // vận tốc góc (rad/s)
  const th0Ref = useRef(0.5)         // góc lúc thả
  const a0Ref = useRef(0.5)          // biên độ góc A₀ (rad) lúc thả
  const traceRef = useRef<{ t: number; th: number }[]>([])
  const tRef = useRef(0)
  const param = useRef({ l, c })
  const raf = useRef<number | null>(null)
  const lastTs = useRef<number | null>(null)
  const playRef = useRef(false)
  const dragRef = useRef(false)
  const firstRef = useRef(false)

  useEffect(() => { param.current = { l, c } }, [l, c])
  useEffect(() => { playRef.current = playing }, [playing])
  const fireFirst = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const Lpix = () => 50 + l * 80     // chiều dài dây hiển thị (px) — gọn để vật không tràn canvas

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#f8fafc'; ctx.fillRect(0, 0, W, H)

    const Lp = Lpix(), th = thRef.current
    const bx = PVX + Lp * Math.sin(th), by = PVY + Lp * Math.cos(th)

    // Giá treo — mép DƯỚI trùng điểm treo (PVY) để dây chạm vào giá
    ctx.fillStyle = '#94a3b8'; ctx.fillRect(PVX - 42, PVY - 9, 84, 9)
    // Đường thẳng đứng (VTCB)
    ctx.strokeStyle = '#cbd5e1'; ctx.setLineDash([4, 4]); ctx.beginPath(); ctx.moveTo(PVX, PVY); ctx.lineTo(PVX, PVY + Lp + 26); ctx.stroke(); ctx.setLineDash([])
    // Cung + nhãn góc
    ctx.strokeStyle = '#7c3aed'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.arc(PVX, PVY, 34, Math.PI / 2, Math.PI / 2 - th, th > 0); ctx.stroke()
    ctx.fillStyle = '#6d28d9'; ctx.font = 'bold 12px sans-serif'; ctx.fillText(`θ=${(th * 180 / Math.PI).toFixed(0)}°`, PVX + 12, PVY + 50)
    // Dây — XUẤT PHÁT đúng điểm treo (PVX, PVY)
    ctx.strokeStyle = '#475569'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(PVX, PVY); ctx.lineTo(bx, by); ctx.stroke()
    // Chốt treo tại điểm treo
    ctx.fillStyle = '#475569'; ctx.beginPath(); ctx.arc(PVX, PVY, 3.5, 0, Math.PI * 2); ctx.fill()
    // Vật
    const g = ctx.createRadialGradient(bx - 5, by - 5, 3, bx, by, 20); g.addColorStop(0, '#fda4af'); g.addColorStop(1, '#e11d48')
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(bx, by, 18, 0, Math.PI * 2); ctx.fill()
    ctx.strokeStyle = '#9f1239'; ctx.lineWidth = 2; ctx.stroke()

    // Lực kéo về = thành phần TIẾP TUYẾN của trọng lực (∝ sinθ), hướng về VTCB (checkbox)
    if (showForce && Math.abs(th) > 0.02) {
      const fmag = Math.min(72, Math.abs(Math.sin(th)) * 95)
      const s2 = -Math.sign(th)                      // ngược chiều tăng |θ|
      const fx = s2 * Math.cos(th) * fmag, fy = s2 * (-Math.sin(th)) * fmag
      const L = Math.hypot(fx, fy) || 1, ux = fx / L, uy = fy / L
      ctx.strokeStyle = '#16a34a'; ctx.fillStyle = '#16a34a'; ctx.lineWidth = 3
      ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx + fx, by + fy); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(bx + fx, by + fy)
      ctx.lineTo(bx + fx - 9 * ux - 5 * uy, by + fy - 9 * uy + 5 * ux)
      ctx.lineTo(bx + fx - 9 * ux + 5 * uy, by + fy - 9 * uy - 5 * ux)
      ctx.closePath(); ctx.fill()
      ctx.font = 'bold 12px sans-serif'; ctx.fillText('F kéo về', bx + fx + ux * 6 - 8, by + fy + uy * 6 + 4)
    }

    // ── Đồ thị θ(t) ── (mũi tên 2 trục + nhãn; số liệu xuống dưới)
    const gx0 = 360, gw = W - gx0 - 24, gAxisY = 150, gAmp = 84
    const yTop = gAxisY - gAmp - 16, xEnd = gx0 + gw
    ctx.strokeStyle = '#475569'; ctx.fillStyle = '#475569'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(gx0, gAxisY); ctx.lineTo(xEnd, gAxisY); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(xEnd, gAxisY); ctx.lineTo(xEnd - 9, gAxisY - 5); ctx.lineTo(xEnd - 9, gAxisY + 5); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(gx0, gAxisY + gAmp + 8); ctx.lineTo(gx0, yTop); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(gx0, yTop); ctx.lineTo(gx0 - 5, yTop + 9); ctx.lineTo(gx0 + 5, yTop + 9); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 12px sans-serif'; ctx.fillText('θ(°)', gx0 - 4, yTop - 4); ctx.fillText('t(s)', xEnd - 6, gAxisY + 18)
    const A0 = a0Ref.current
    const tr = traceRef.current
    const tEnd = tRef.current, tStart = Math.max(0, tEnd - WIN)
    const pMap = (p: { t: number; th: number }) => ({
      gx: gx0 + ((p.t - tStart) / WIN) * gw,
      gy: gAxisY - Math.max(-1.1, Math.min(1.1, p.th / THMAX)) * gAmp,
    })
    if (c > 0 && tr.length > 2) {
      // CÓ CẢN NHỚT → biên độ giảm HÀM MŨ. Nối ĐÚNG các đỉnh thật (đường cong), kéo dài từ trục tung (0, ±A₀).
      const ups: { t: number; v: number }[] = [{ t: 0, v: A0 }]
      const downs: { t: number; v: number }[] = [{ t: 0, v: -A0 }]
      for (let i = 1; i < tr.length - 1; i++) {
        const p = tr[i].th, a = tr[i - 1].th, c2 = tr[i + 1].th
        if (p > a && p >= c2) ups.push({ t: tr[i].t, v: p })
        else if (p < a && p <= c2) downs.push({ t: tr[i].t, v: p })
      }
      // ngoại suy đoạn cuối tới tEnd → đường bao chạy THEO THỜI GIAN, kẹp về 0 (cản nhớt)
      const extend = (pts: { t: number; v: number }[], sgn: number) => {
        if (pts.length < 2) return
        const b = pts[pts.length - 1], a = pts[pts.length - 2]
        if (b.t < tEnd - 1e-3) {
          const slope = (b.v - a.v) / Math.max(1e-3, b.t - a.t)
          let ve = b.v + slope * (tEnd - b.t)
          ve = sgn > 0 ? Math.max(0, ve) : Math.min(0, ve)
          pts.push({ t: tEnd, v: ve })
        }
      }
      extend(ups, 1); extend(downs, -1)
      const env = (pts: { t: number; v: number }[]) => {
        const w = pts.filter((p) => p.t >= tStart - 1e-3)
        if (w.length < 2) return
        ctx.strokeStyle = 'rgba(244,63,94,0.75)'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4]); ctx.beginPath()
        w.forEach((p, k) => {
          const gx = gx0 + ((p.t - tStart) / WIN) * gw
          const gy = gAxisY - Math.max(-1.1, Math.min(1.1, p.v / THMAX)) * gAmp
          k ? ctx.lineTo(gx, gy) : ctx.moveTo(gx, gy)
        })
        ctx.stroke(); ctx.setLineDash([])
      }
      env(ups); env(downs)
    } else {
      const ay = (A0 / THMAX) * gAmp
      ctx.strokeStyle = 'rgba(244,63,94,0.35)'; ctx.setLineDash([5, 4])
      ctx.beginPath(); ctx.moveTo(gx0, gAxisY - ay); ctx.lineTo(gx0 + gw, gAxisY - ay); ctx.moveTo(gx0, gAxisY + ay); ctx.lineTo(gx0 + gw, gAxisY + ay); ctx.stroke(); ctx.setLineDash([])
    }
    if (tr.length > 1) {
      ctx.strokeStyle = '#e11d48'; ctx.lineWidth = 2; ctx.beginPath(); let started = false
      for (const p of tr) {
        if (p.t < tStart) continue
        const g = pMap(p)
        started ? ctx.lineTo(g.gx, g.gy) : (ctx.moveTo(g.gx, g.gy), started = true)
      }
      ctx.stroke()
    }

    // Số liệu
    const T = 2 * Math.PI * Math.sqrt(l / G)
    ctx.fillStyle = '#0f172a'; ctx.font = 'bold 12px sans-serif'
    ctx.fillText(`T = 2π√(l/g) = ${T.toFixed(2)} s`, gx0, 300)
    ctx.fillStyle = '#dc2626'; ctx.fillText(`Biên độ θ₀ = ${(th0Ref.current * 180 / Math.PI).toFixed(0)}° · A₀ = ${(A0 * 180 / Math.PI).toFixed(0)}° (v₀ = ${v0} m/s)`, gx0, 322)
    if (c > 0) { ctx.fillStyle = '#7c3aed'; ctx.fillText('cản nhớt → tắt dần (biên độ giảm hàm mũ)', gx0, 342) }
  }, [l, c, v0, showForce])

  const loop = useCallback((ts: number) => {
    if (!playRef.current) return
    if (lastTs.current == null) lastTs.current = ts
    let dt = (ts - lastTs.current) / 1000; lastTs.current = ts; dt = Math.min(dt, 0.03)
    const { l: ll, c: cc } = param.current
    let th = thRef.current, om = omRef.current
    const alpha = -(G / ll) * Math.sin(th) - cc * om
    om += alpha * dt; th += om * dt
    if (th > THMAX) { th = THMAX; om = -om * 0.4 } else if (th < -THMAX) { th = -THMAX; om = -om * 0.4 }
    thRef.current = th; omRef.current = om
    tRef.current += dt
    traceRef.current.push({ t: tRef.current, th })
    if (traceRef.current.length > 2000) traceRef.current.shift()
    draw()
    if (playRef.current) raf.current = requestAnimationFrame(loop)
  }, [draw])

  useEffect(() => {
    if (playing) { lastTs.current = null; raf.current = requestAnimationFrame(loop) }
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [playing, loop])
  useEffect(() => { if (!playing) draw() }, [draw, playing])

  const launch = () => {
    fireFirst()
    const om0 = v0 / l                     // vận tốc góc đầu = v_tt / l
    thRef.current = th0Ref.current; omRef.current = om0; tRef.current = 0; traceRef.current = []
    const wn = Math.sqrt(G / l)
    a0Ref.current = Math.min(THMAX, Math.sqrt(th0Ref.current * th0Ref.current + (om0 / wn) * (om0 / wn)))
    setPlaying(true)
  }

  // Kéo vật để đặt θ₀
  const toLogic = (e: React.PointerEvent) => { const r = cv.current!.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width * W, y: (e.clientY - r.top) / r.height * H } }
  const onDown = (e: React.PointerEvent) => {
    const p = toLogic(e); const Lp = Lpix(); const th = thRef.current
    const bx = PVX + Lp * Math.sin(th), by = PVY + Lp * Math.cos(th)
    if (Math.hypot(p.x - bx, p.y - by) < 32) { dragRef.current = true; setPlaying(false); fireFirst(); (e.target as HTMLElement).setPointerCapture?.(e.pointerId) }
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const p = toLogic(e)
    let th = Math.atan2(p.x - PVX, p.y - PVY)        // góc so với phương thẳng đứng
    th = Math.max(-THMAX, Math.min(THMAX, th))
    thRef.current = th; th0Ref.current = th; a0Ref.current = Math.abs(th); omRef.current = 0; draw()
  }
  const onUp = () => { if (dragRef.current) { dragRef.current = false; launch() } }
  const reset = () => { setPlaying(false); thRef.current = 0; th0Ref.current = 0; a0Ref.current = 0; omRef.current = 0; traceRef.current = []; tRef.current = 0; draw() }

  const sf = <T,>(s: (v: T) => void) => (v: T) => { fireFirst(); s(v) }

  return (
    <div className="rounded-2xl border border-rose-200 bg-white p-4 my-3">
      <h4 className="font-bold text-rose-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab active:cursor-grabbing" />
      <div className="flex items-center justify-center gap-2 my-3">
        <button onClick={launch} className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-bold hover:bg-rose-700">▶ Thả / Chạy lại</button>
        <button onClick={() => setPlaying(false)} className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">⏸ Dừng</button>
        <button onClick={reset} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200">↺ Về VTCB</button>
      </div>
      <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer"><input type="checkbox" checked={showForce} onChange={(e) => { fireFirst(); setShowForce(e.target.checked) }} /> <span className="text-emerald-700 font-semibold">Hiện lực kéo về (thành phần tiếp tuyến của trọng lực)</span></label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Chiều dài l</span><input type="range" min={0.5} max={2.5} step={0.1} value={l} onChange={(e) => sf(setL)(Number(e.target.value))} className="flex-1" /><span className="w-14 text-right font-semibold">{l.toFixed(1)} m</span></label>
        <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Hệ số cản</span><input type="range" min={0} max={2} step={0.05} value={c} onChange={(e) => sf(setC)(Number(e.target.value))} className="flex-1" /><span className="w-14 text-right font-semibold">{c.toFixed(2)}</span></label>
        <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Vận tốc đầu v₀</span><input type="range" min={0} max={3} step={0.5} value={v0} onChange={(e) => sf(setV0)(Number(e.target.value))} className="flex-1" /><span className="w-14 text-right font-semibold">{v0} m/s</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Kéo quả nặng lệch khỏi phương thẳng đứng rồi thả. v₀=0 → biên độ đúng bằng θ₀; v₀&gt;0 → biên độ lớn hơn. Đổi l để thấy chu kì đổi; đổi hệ số cản để thấy tắt dần.</p>
    </div>
  )
}

export default SimplePendulumSimulation
